import Polygon from "@arcgis/core/geometry/Polygon"
import globalLayers from "../CustomerMaps/Maps/GlobaLMap"
import axios, { AxiosResponse } from "axios";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import Graphic from "@arcgis/core/Graphic";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import { getsimpleFillSymbol } from "../CustomerMaps/MarkerSymbols/MarkerSymbols";
import { store } from "../../ReduxTool/store/store";
import { setPolygonDrawn, setTitle, toggleDrawer } from "../../ReduxTool/Slice/Drawer/DrawerReducer";
import { obstructionArea, setUseableArea, showEsriDrawTool } from "../../ReduxTool/Slice/Consumer/ConsumerReducers";
import { setInformationModal, setIsBuildingDrawn, toggleRoofConfirmBtn } from "../../ReduxTool/Slice/Map/MapReducer";
import { LayerProperties } from "./geoservertypes";
import { WFSURL, WFSURLParentId } from "./getWFSUrls";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import { convertGeometrySpatialRefs } from "../../lib/CordinatesConversion/convertGeometrySpatialRefs";
import { getPolygonSymbol } from "../../lib/symbols/getSymbols";

export const buildingBase = 'building'

type featureLyarGeo = {
    bbox: any
    geometry: any
    geometry_name: string,
    id: string,
    properties: LayerProperties,

}

export type geoServerObject = {
    bbox: number[]
    crs: any
    features: featureLyarGeo[]
    numberMatched: number
    numberReturned: number
    timeStamp: number
    totalFeatures: number
    type: string
}

/**
 * Fetch all roofs for a building base id, subtract each roof's keepouts/obstructions,
 * then combine (union) all cleaned roofs into a single Polygon and return it.
 *
 * @param buildingBaseId - id of the building base layer (parent id)
 * @returns Polygon | null - combined usable roof geometry after subtracting obstructions
 */
export const fetchAndCombineRoofsWithKeepouts = async (buildingBaseId: string): Promise<Polygon | null> => {
  // Helpers
  const safeSubtract = (base: Polygon, sub: Polygon): Polygon => {
    try {
      const diff = geometryEngine.difference(base, sub);
      return (diff as Polygon) ?? base;
    } catch (err) {
      console.warn("geometry difference failed, returning base geometry", err);
      return base;
    }
  };

  const safeUnionMany = (polygons: Polygon[]): Polygon | null => {
    try {
      if (!polygons?.length) return null;
      // geometryEngine.union accepts array or two args depending on version; perform pairwise union
      let current = polygons[0];
      for (let i = 1; i < polygons.length; i++) {
        const next = polygons[i];
        const unioned = geometryEngine.union([current, next]) as Polygon;
        if (unioned) current = unioned;
      }
      return current;
    } catch (err) {
      console.error("geometry union failed:", err);
      return null;
    }
  };

  const fetchFeatures = async (wfsUrl: string): Promise<featureLyarGeo[]> => {
    try {
      const resp: AxiosResponse<geoServerObject> = await axios.get(wfsUrl, { timeout: 15000 });
      return resp?.data?.features ?? [];
    } catch (err) {
      console.warn("WFS fetch failed:", wfsUrl, err);
      return [];
    }
  };

  const featureToPolygon = (feature: featureLyarGeo): Polygon | null => {
    try {
      // handle both old (coordinates[0]) and new (coordinates) shapes
      const rawCoords = feature?.geometry?.coordinates;
      if (!rawCoords) return null;

      // canonicalize to rings array of [ [x,y,0], ... ]
      // If coordinates[0] is a ring array, use it; otherwise assume coordinates is already rings
      const rings: any[] = Array.isArray(rawCoords[0]) && typeof rawCoords[0][0] === "number"
        ? [ (rawCoords as number[][]).map(p => [p[0], p[1], 0]) ]
        : (Array.isArray(rawCoords) ? (rawCoords as any).map((ring: any) => ring.map((pt: any) => [pt[0], pt[1], 0])) : []);

      if (!rings.length) return null;
      return new Polygon({ rings });
    } catch (err) {
      console.warn("featureToPolygon failed for feature:", feature?.id, err);
      return null;
    }
  };

  try {
    const { wmsLayers } = globalLayers;
    if (!wmsLayers) {
      console.error("No globalLayers.wmsLayers configured");
      return null;
    }

    const buildingLayerName = wmsLayers.buildingname;
    if (!buildingLayerName) {
      console.error("No building layer configured (wmsLayers.buildingname)");
      return null;
    }

    // 1. fetch all building features for the buildingBaseId (these are building footprints which contain roofs)
    const buildingWfsUrl = WFSURLParentId(buildingLayerName, buildingBaseId);
    const buildingFeatures = await fetchFeatures(buildingWfsUrl);
    if (!buildingFeatures.length) {
      console.warn("No building features found for id:", buildingBaseId);
      return null;
    }

    // Collect cleaned roofs here
    const cleanedRoofPolygons: Polygon[] = [];

    // For each building feature, fetch roof(s) that belong to that building
    for (const buildingFeature of buildingFeatures) {
      // buildingFeature.id may be like layerName.x; determine building id if needed
      const building_feature_id = buildingFeature?.id?.split?.('.')?.[1] ?? null;
      // Use the buildingFeature id (if roof WFS expects building id parent)
      // Fetch all roofs for this buildingFeature (using roofLayers configured)
      const roofLayerNames = wmsLayers.roofLayers ?? [];
      if (!roofLayerNames.length) continue;

      // We'll fetch roofs for each roofLayer configured (some setups have multiple roof layers)
      const roofFetchPromises = roofLayerNames.map(async (roofLayerName: string) => {
        // WFS call for roof layer by parent building_feature_id (same approach you used)
        const roofWfs = WFSURLParentId(roofLayerName, building_feature_id);
        const roofFeatures = await fetchFeatures(roofWfs);
        return { roofLayerName, roofFeatures };
      });

      const roofsByLayer = await Promise.all(roofFetchPromises);

      // Process each roof feature found
      for (const { roofLayerName, roofFeatures } of roofsByLayer) {
        for (const roofFeature of roofFeatures) {
          // Convert roof feature -> Polygon
          const roofPolyRaw = featureToPolygon(roofFeature);
          if (!roofPolyRaw) continue;

          // Convert geometry spatial refs to map/WebMercator if helper exists
          // prefer convertGeometrySpatialRefs if available; otherwise try globalLayers.convertCoordinateSystem
        //   const convert = typeof (global as any).convertGeometrySpatialRefs === 'function'
        //     ? (global as any).convertGeometrySpatialRefs
        //     : (globalLayers.convertCoordinateSystem ?? null);

          const convertedRoofGeom = await convertGeometrySpatialRefs(roofPolyRaw, 102100);
          if (!convertedRoofGeom) continue;

          // Start with this roof as the base usable geometry
          let usableRoofGeom = convertedRoofGeom as Polygon;

          // 2. For the current roof, build list of child layers to fetch (all layerNames_new except the roof layer)
          const allChildLayerNames = (wmsLayers.layerNames_new ?? []).filter(n => n !== roofLayerName);

          // Always include metalRoofObs for metal roof types if configured
        //   const isMetal = roofLayerName.toLowerCase().includes("metal_roof") || roofLayerName.toLowerCase().includes("metal");
        //   const metalObsLayer = wmsLayers.metalRoofObs;
        //   if (isMetal && metalObsLayer) allChildLayerNames.push(metalObsLayer);

          // 3. Fetch all child layers for this roof in parallel
          const childFetchPromises = allChildLayerNames.map(async (childLayerName) => {
            // WFS by parent roof id
            const roofId = roofFeature?.id?.split?.('.')?.[1] ?? roofFeature?.properties?.Id ?? null;
            if (!roofId) return [];

            const childWfs = WFSURLParentId(childLayerName, roofId);
            const childFeatures = await fetchFeatures(childWfs);
            return childFeatures.map(f => ({ layerName: childLayerName, feature: f }));
          });

          const childFetchResults = await Promise.all(childFetchPromises);
          const flattenedChildItems = childFetchResults.flat();

          // 4. For each child item, convert to polygon and subtract from usableRoofGeom
          // process sequentially to avoid heavy union/difference operations concurrently
          for (const { layerName, feature } of flattenedChildItems) {
            const childPolyRaw = featureToPolygon(feature);
            if (!childPolyRaw) continue;

            const convertedChild = await convertGeometrySpatialRefs(childPolyRaw, 102100);

            if (!convertedChild) continue;

            // Subtract keepout/obstruction from roof usable geometry
            usableRoofGeom = safeSubtract(usableRoofGeom, convertedChild as Polygon);
          }

          // After subtracting all children, add usableRoofGeom to list if it has rings
          if (usableRoofGeom && usableRoofGeom.rings && usableRoofGeom.rings.length) {
            cleanedRoofPolygons.push(usableRoofGeom);
          }
        } // end for each roofFeature
      } // end for each roofsByLayer
    } // end for each buildingFeature

    // 5. Combine all cleaned roofs into a single polygon (union)
    const combined = safeUnionMany(cleanedRoofPolygons);
    if (!combined) {
      console.warn("No cleaned roofs were generated");
      return null;
    }

    // Optional: set some globals for downstream code (mirrors earlier code behaviour)
    // globalLayers.combinedUsableRoofGeometry = combined;
    return combined;
  } catch (err) {
    console.error("fetchAndCombineRoofsWithKeepouts failed:", err);
    return null;
  }
};

export const fetchBuildingFromCoordinates = async (
  latitude: number,
  longitude: number
): Promise<Polygon | null> => {
  try {
    // ✅ 1️⃣ Validate inputs early
    if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
      console.warn("Invalid coordinates supplied:", latitude, longitude);
      return null;
    }

    // ✅ 2️⃣ Get layer and type info once
    const { wmsLayers, map, selectedRoof } = globalLayers;
    const { baselayer, type } = wmsLayers || {};
    if (!baselayer || !type) {
      console.warn("Missing WMS layer configuration");
      return null;
    }

    // ✅ 3️⃣ Prepare CQL filter and WFS URL
    const cqlFilter = `INTERSECTS(${type}, POINT (${latitude} ${longitude}))`;
    const wfsUrl = WFSURL(baselayer, cqlFilter);

    // ✅ 4️⃣ Fetch data (short timeout & proper error handling)
    const response: AxiosResponse<geoServerObject> = await axios.get(wfsUrl, {
      timeout: 10000,
      headers: { Accept: "application/json" },
    });

    if (response.status !== 200 || !response.data?.features?.length) {
      console.warn("No building features found at this location");
      return null;
    }

    // ✅ 5️⃣ Extract and store relevant info
    const feature = response.data.features[0];
    const geometry = feature?.geometry;
    const coordinates = geometry?.coordinates?.[0];

    if (!coordinates?.length) {
      console.warn("Feature geometry missing or invalid");
      return null;
    }

    selectedRoof.objectid = feature.id?.split(".")[1] ?? null;
    selectedRoof.properties = feature.properties ?? {};

    // ✅ 6️⃣ Construct Polygon geometry
    const polygon = new Polygon({ rings: coordinates });

    // ✅ 7️⃣ Update GeoJSONLayer (with safe cleanup)
    const blob = new Blob([JSON.stringify(response.data)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const layerTitle = "GeoJson_Layer";

    const existingLayer = map?.allLayers.find(
      (l: any) => l.title === layerTitle
    );
    if (existingLayer) map?.remove(existingLayer);

    const geoJsonLayer = new GeoJSONLayer({ url, title: layerTitle });
    map?.add(geoJsonLayer);

    return polygon;
  } catch (error) {
    console.error("Error fetching building:", error);
    return null;
  }
};

//  for fetching the roofs and theire keepouts when the building is drawn manually in cosumer part 
export const fetch3dData = async (latitude: number, longitude: number) => {
    store.dispatch(toggleRoofConfirmBtn(false))
    try {
      const data = await fetchBuildingFromCoordinates(latitude, longitude);
      if (!data) {
        return null; // Return false if no data is found
      }
  
      // Convert the WKID 4326 to 102100
      const convertedGeom = await globalLayers.convertCoordinateSystem(
        data,
        102100
      );
      if (!convertedGeom) {
        return null; // Return false if conversion fails
      }


      // now call all the roofs and theire keepout and cutt the keepouts from their roof geometry
      const totalRoofArea = geometryEngine.geodesicArea(convertedGeom as Polygon, "square-meters");
      const totalArea = totalRoofArea.toFixed(2)

      const cutoffGeometry =  await fetchAndCombineRoofsWithKeepouts(globalLayers.selectedRoof.objectid)

      
      if (!cutoffGeometry) return null;
      const graphic = new Graphic({
        geometry: cutoffGeometry,
        symbol: getPolygonSymbol(),
        attributes: {
            name: 'building'
        }
      });

  
        const geodesicArea = geometryEngine.geodesicArea(cutoffGeometry as Polygon, "square-meters");
        const useAbleArea = geodesicArea.toFixed(2)


        // store.dispatch(setToolTipModal({ state: true, title: "Precise Analysis", content: "Let's work together to find the perfect solar solution for your home! To proceed, please fill out all details on the left." }))
        store.dispatch(setInformationModal({state:true,title:"Precise Analysis",content:"Let's work together to find the perfect solar solution for your home! To proceed, please fill out all details on the left."}))            
        store.dispatch(setTitle("projectsetup"))
        store.dispatch(toggleDrawer(true))
        store.dispatch(obstructionArea({ obsArea: useAbleArea, useablearea:  useAbleArea }))
        store.dispatch(showEsriDrawTool(false))
        store.dispatch(setPolygonDrawn('1'))
        store.dispatch(setIsBuildingDrawn(true))
        globalLayers.isBuildingThere = true
        globalLayers.totalroofArea = parseFloat(useAbleArea);
  
        globalLayers.sketchLayers?.graphics.add(graphic)
        globalLayers.selectedGraphic = graphic

        // Navigate to the specified location
        const centroid = (graphic.geometry as Polygon).centroid
        globalLayers.goto(centroid.latitude, centroid.longitude);

  
      // Cancel any active SketchViewModel interaction after a delay
      setTimeout(() => {
        globalLayers.sketchVM.cancel();
      }, 2000);
  
      return cutoffGeometry; // Return true if everything executes successfully
    } catch (e) {
      console.error("Error in handleBuildingGeoLocation:", e);
      return null; // Return false if any error occurs
    }
};
